/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
// we need to use tabindex on popup ^^ so it could be interactable right away
// when it's appears
import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import InlineSVG from 'svg-inline-react';
import assess from '../../../assets/images/assess.svg';
import { useAppContext } from '../../../common/contexts/AppContext';
import { linkOnClickHandler } from '../../../common/navigation/navigation';
import { localize } from '../../../common/localization';
import { handleEnterKeyPress } from '../../../common/popups/utils';

// All labels for assess links should start
// with capital letter. There is a special case
// for Spanish measures with 'recommendada' label.
// This word should always start with capital letter
// in assess icon component and regular letter in
// recommendation dropdown. Localization library
// doesn't allow to store translation for both
// cases.
const capitalizeLinkLabel = word => word[0].toUpperCase() + word.slice(1);

const AssessLink = ({ mode, path, onClickHandler, onKeyDown = handleEnterKeyPress }) => {
    const { checkAndGetSessionStatus } = useAppContext();

    const cssClasses = classNames(
        'dashboard-assess-icon-item', // unique selector for selenium tests
        // HACK: empty string is added for correct prefixing
        // issue https://github.com/vezetvsem/react-classname-prefix-loader/issues/3
        'dashboard-assess-link',
        'dashboard-' + mode,
    );
    const label = (
        <>
            <InlineSVG src={assess} className='dashboard-assess-icon' />
            {capitalizeLinkLabel(localize(AssessLink.TEXT[mode]))}
        </>
    );

    const tabindex = mode.includes(AssessLink.MODE.DISABLED) ? -1 : 0;

    if (mode === AssessLink.MODE.DISCONTINUE || mode === AssessLink.MODE.TESTED_OUT) {
        return (
            <button
                type="button"
                className={cssClasses}
                onClick={onClickHandler}
                onKeyDown={onKeyDown}
                tabIndex={tabindex}
            >
                {label}
            </button>
        );
    }

    if (!path && !onClickHandler) {
        return <div className={cssClasses}>{label}</div>;
    }

    if (!path && onClickHandler) {
        return (
            <div
                className={cssClasses}
                onClick={onClickHandler}
                onKeyDown={onKeyDown}
                tabIndex={tabindex}
            >
                {label}
            </div>
        );
    }

    return (
        <a
            className={cssClasses}
            href={path}
            onClick={onClickHandler || linkOnClickHandler(path, checkAndGetSessionStatus)}
            onKeyDown={onKeyDown}
            tabIndex={tabindex}
        >
            {label}
        </a>
    );
};

AssessLink.MODE = {
    REQUIRED: 'required',
    RESUME: 'resume',
    OPTIONAL: 'optional',
    DISABLED: 'disabled',
    OPTIONAL_DISABLED: 'optional-disabled',
    DISCONTINUE: 'discontinued',
    TESTED_OUT: 'tested-out',
    DISCONTINUE_DISABLED: 'discontinue-disabled',
    TESTED_OUT_DISABLED: 'tested-out-disabled',
    RESUME_DISABLED: 'resume-disabled',
    RECOMMENDED: 'recommended',
    RECOMMENDED_DISABLED: 'recommended-disabled',
};

AssessLink.TEXT = {
    [AssessLink.MODE.REQUIRED]: 'Assess',
    [AssessLink.MODE.DISABLED]: 'Assess',
    [AssessLink.MODE.RESUME]: 'Resume',
    [AssessLink.MODE.OPTIONAL]: 'Optional',
    [AssessLink.MODE.OPTIONAL_DISABLED]: 'Optional',
    [AssessLink.MODE.DISCONTINUE]: 'Discontinued',
    [AssessLink.MODE.TESTED_OUT]: 'Tested Out',
    [AssessLink.MODE.DISCONTINUE_DISABLED]: 'Discontinued',
    [AssessLink.MODE.TESTED_OUT_DISABLED]: 'Tested Out',
    [AssessLink.MODE.RESUME_DISABLED]: 'Resume',
    [AssessLink.MODE.RECOMMENDED]: 'Recommended',
    [AssessLink.MODE.RECOMMENDED_DISABLED]: 'Recommended',
};

AssessLink.propTypes = {
    path: PropTypes.string,
    mode: PropTypes.oneOf([
        AssessLink.MODE.REQUIRED,
        AssessLink.MODE.RESUME,
        AssessLink.MODE.OPTIONAL,
        AssessLink.MODE.OPTIONAL_DISABLED,
        AssessLink.MODE.DISABLED,
        AssessLink.MODE.DISCONTINUE,
        AssessLink.MODE.TESTED_OUT,
        AssessLink.MODE.TESTED_OUT_DISABLED,
        AssessLink.MODE.DISCONTINUE_DISABLED,
        AssessLink.MODE.RESUME_DISABLED,
        AssessLink.MODE.RECOMMENDED,
        AssessLink.MODE.RECOMMENDED_DISABLED,
    ]),
    onClickHandler: PropTypes.func,
    onKeyDown: PropTypes.func,
};

AssessLink.defaultProps = {
    mode: AssessLink.MODE.REQUIRED,
    path: undefined,
    onClickHandler: undefined,
    onKeyDown: undefined,
};

export default AssessLink;
